import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../utils/api';
import { errorMessage, renderable } from '../utils/errorMessage';
// Phase 7 sign-off: profiles email, display_name, short_text, long_text.
import { MAX_LENGTHS } from '../utils/inputValidation';
import { useTranslation } from 'react-i18next';
import SessionManager from '../components/SessionManager';
import AccountTab from './settings/AccountTab';
import PaymentsTab from './settings/PaymentsTab';
import { VPCard, Label, Row } from './settings/primitives';
import { REFUND_POLICY_SUMMARY } from '../constants/legal';
import RefundRequestCard from '../components/RefundRequestCard';
// DASH, not a local em-dash literal: the panel below already owns the glyph for
// "we do not know" (see its O3 note), and the membership card sits directly
// under it. Two definitions of that placeholder on one screen is how they
// drift apart.
import OrgCreditPanel, { DASH } from '../components/OrgCreditPanel';
// The PHRASING only, not the numbers. '/' and '/pricing' state the monthly
// allowance through this helper; the account page saying the same fact in a
// different sentence is how a customer starts wondering which one is real.
// ⚠ PLAN_MONTHLY_CREDITS is deliberately NOT imported here -- that map is the
// marketing tier table, and this screen is about the customer's ACTUAL
// membership, which only the server knows. See planAllowanceText.
import { monthlyCreditsLine } from '../constants/plans';

const BASE_TABS = [
  { key: 'profile', icon: 'person', labelEn: 'Profile', labelKo: '프로필' },
  { key: 'subscription', icon: 'credit_card', labelEn: 'Subscription', labelKo: '구독' },
  // BASE_TABS, not ACCOUNT_TAB: that one is gated on isCustomer, which excludes
  // testers and admins. The data is self-scoped for every role, so gating it
  // would only hide a tester's own payment history from them.
  { key: 'payments', icon: 'receipt_long', labelEn: 'Payments', labelKo: '결제' },
  { key: 'team', icon: 'group', labelEn: 'Team', labelKo: '팀' },
  { key: 'notifications', icon: 'notifications', labelEn: 'Notifications', labelKo: '알림' },
  { key: 'support', icon: 'help', labelEn: 'Support', labelKo: '지원' },
];
const ACCOUNT_TAB = { key: 'account', icon: 'manage_accounts', labelEn: 'Account', labelKo: '계정' };

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const { user } = useAuth();
  const isCustomer = !!user && !user.is_root && !user.can_manage_admins && user.role !== 'tester';
  const tabs = isCustomer ? [...BASE_TABS, ACCOUNT_TAB] : BASE_TABS;

  const setTab = (key) => setSearchParams({ tab: key });

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" style={{ color: 'var(--text-primary)' }}>
      <h1 className="font-headline font-extrabold text-2xl mb-6" style={{ color: 'var(--text-primary)' }}>
        {isKo ? '설정' : 'Settings'}
      </h1>

      {/* Tab bar */}
      <div className="flex gap-1 mb-8 overflow-x-auto pb-1" style={{ borderBottom: '1px solid var(--border-color)' }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setTab(tab.key)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors"
            style={{
              borderBottom: activeTab === tab.key ? '2px solid #a100aa' : '2px solid transparent',
              color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-secondary)',
              marginBottom: -1,
              background: 'transparent',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{tab.icon}</span>
            {isKo ? tab.labelKo : tab.labelEn}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'subscription' && <SubscriptionTab />}
      {activeTab === 'payments' && <PaymentsTab />}
      {activeTab === 'team' && <TeamTab />}
      {activeTab === 'notifications' && <NotificationsTab />}
      {activeTab === 'support' && <SupportTab />}
      {activeTab === 'account' && isCustomer && <AccountTab />}
    </div>
  );
}

/* ─── Profile Tab ────────────────────────────────────────── */
function ProfileTab() {
  const { user, updatePreferredLang } = useAuth();
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [fullName, setFullName] = useState('');
  const [langSaving, setLangSaving] = useState(false);
  const [langMsg, setLangMsg] = useState('');
  const currentLang = (user?.preferred_lang === 'en' || user?.preferred_lang === 'ko')
    ? user.preferred_lang
    : (i18n.language === 'en' ? 'en' : 'ko');

  const loadProfile = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/profile');
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setFullName(data.full_name || '');
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await apiFetch('/api/v1/profile', { method: 'PUT', body: JSON.stringify({ full_name: fullName }) });
      if (res.ok) setMsg(isKo ? '저장되었습니다' : 'Saved');
      else setMsg(isKo ? '저장 실패' : 'Save failed');
    } catch { setMsg('Error'); }
    setSaving(false);
  };

  if (loading) return <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>;

  const handleChangeLang = async (newLang) => {
    if (newLang === currentLang) return;
    setLangSaving(true); setLangMsg('');
    try {
      await updatePreferredLang(newLang);
      setLangMsg(isKo ? (newLang === 'ko' ? '언어가 한국어로 변경되었습니다' : '언어가 영어로 변경되었습니다')
                      : (newLang === 'en' ? 'Language changed to English' : 'Language changed to Korean'));
    } catch (err) {
      setLangMsg(err.message || 'Failed');
    }
    setLangSaving(false);
  };

  return (
    <div className="space-y-6">
      <VPCard title={isKo ? '개인 정보' : 'Personal Information'}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>{isKo ? '이름' : 'Full Name'}</Label>
            <input className="vp-input" value={fullName} onChange={e => setFullName(e.target.value)} maxLength={MAX_LENGTHS.display_name} />
          </div>
          <div>
            <Label>{isKo ? '이메일' : 'Email'}</Label>
            <input className="vp-input" value={user?.email || ''} disabled style={{ opacity: 0.5 }} />
          </div>
          <div>
            <Label>{isKo ? '역할' : 'Role'}</Label>
            <input className="vp-input" value={user?.role || ''} disabled style={{ opacity: 0.5 }} />
          </div>
          <div>
            <Label>{isKo ? '조직' : 'Organization'}</Label>
            <input className="vp-input" value={profile?.organization_name || ''} disabled style={{ opacity: 0.5 }} />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-5">
          <button onClick={handleSave} disabled={saving} className="vp-btn-primary text-sm px-5 py-2">{saving ? '…' : isKo ? '저장' : 'Save'}</button>
          {msg && <span className="text-sm" style={{ color: '#16A34A' }}>{msg}</span>}
        </div>
      </VPCard>

      <VPCard title={isKo ? '언어 설정' : 'Language'}>
        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          {isKo ? '이 설정은 앱 표시 언어와 이메일 알림 언어 모두에 적용됩니다.' : 'This setting controls both the app display language and the language of email notifications.'}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center" style={{ border: '1px solid var(--border-color)', borderRadius: 20, padding: 2 }}>
            <button
              type="button"
              onClick={() => handleChangeLang('ko')}
              disabled={langSaving}
              className="text-xs font-bold transition-all"
              style={{ padding: '6px 16px', borderRadius: 20, background: currentLang === 'ko' ? '#a100aa' : 'transparent', color: currentLang === 'ko' ? '#fff' : 'var(--text-secondary)', cursor: langSaving ? 'wait' : 'pointer' }}
            >한국어</button>
            <button
              type="button"
              onClick={() => handleChangeLang('en')}
              disabled={langSaving}
              className="text-xs font-bold transition-all"
              style={{ padding: '6px 16px', borderRadius: 20, background: currentLang === 'en' ? '#a100aa' : 'transparent', color: currentLang === 'en' ? '#fff' : 'var(--text-secondary)', cursor: langSaving ? 'wait' : 'pointer' }}
            >English</button>
          </div>
          {langMsg && <span className="text-sm" style={{ color: '#16A34A' }}>{langMsg}</span>}
        </div>
      </VPCard>

      <VPCard title={isKo ? '활성 세션' : 'Active Sessions'}>
        <SessionManager />
      </VPCard>
    </div>
  );
}

/* ─── Subscription Tab ───────────────────────────────────── */
function SubscriptionActions({ membership, isKo }) {
  const navigate = useNavigate();
  const tier = (membership?.tier || membership?.tier_name || '').toLowerCase();
  const isTrial = tier === 'trial' || tier === '' || !membership;
  const planLabel = isTrial
    ? (isKo ? '플랜 업그레이드' : 'Upgrade plan')
    : (isKo ? '플랜 변경' : 'Change plan');

  const btn = {
    padding: '10px 18px',
    borderRadius: 6,
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  };

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <button
        type="button"
        onClick={() => navigate('/profile/manage-membership')}
        style={{
          ...btn,
          background: 'linear-gradient(135deg, #931DA4 0%, #123293 100%)',
          color: '#FFFFFF',
        }}
      >
        {planLabel}
      </button>
      {/* The "크레딧 충전" button was REMOVED 2026-09-03 (T0.1a). It sent the
          customer to ?mode=topup, whose only purchasable products were the
          credit packs — now withdrawn. It would have opened a page with
          nothing on it. Constraint: never render a control that cannot
          complete. Extra credits are a sales conversation until 정기결제
          onboarding lands. */}
    </div>
  );
}

/**
 * WHAT THE PLAN INCLUDES -- in one phrase, for the row beside the price.
 *
 * The 구독 tab stated a tier name and a price and nothing about the entitlement
 * behind them, so a customer could read the whole membership card without ever
 * learning that Starter buys 200 검증 a month. The product owner asked for the
 * allowance next to the price on 2026-09-28, on this tab and on the landing
 * pricing card.
 *
 * ⚠ THIS IS THE ENTITLEMENT, NOT THE BALANCE. <OrgCreditPanel> above prints the
 * pool that is LEFT this period; this prints what the plan puts in it every
 * month. Two different numbers under the same words on one screen is how a
 * customer concludes we are lying to them, so the label says 포함 / included and
 * the value says 매월 / each month, while the panel's own headline says 남은 /
 * left. They are never the same sentence.
 *
 * ⚠ AND IT IS A SERVICE QUANTITY, NOT A CREDIT (KG이니시스 review, 2026-10-02).
 * KG classes "크레딧" as 환금성, so the unit is 분석 리포트 N건 (plan §3), and
 * the monthly phrasing is borrowed from constants/plans.js like before.
 *
 * ⚠ SERVER-SOURCED, NOT A CLIENT TIER TABLE. `creditLimit` is
 * `org_credits.credit_limit` from GET /profile/membership -- `membership_tiers.
 * credit_limit` for THIS org's active membership (api/profile.py, the org_row
 * query). A frontend {starter: 200, pro: 600} map would be a second price list
 * that drifts, and on a billing surface drift means telling a customer they
 * bought something they did not.
 *
 * ⚠ AND IT IS A CEILING, NOT PROOF OF A GRANT. The amount the anniversary sweep
 * actually writes comes from product_catalogue.monthly_credits (read by
 * plan_credit_service.monthly_credits_for_tier), and most assignable tiers --
 * trial, enterprise, studio, fgi_* -- carry no monthly allowance at all while
 * still holding an 'active' membership row. So "매월 … 제공" is claimed ONLY when
 * the server says this tier grants monthly (`tier_grants_monthly`, decided in
 * api/profile.py precisely so the tier list cannot drift into the browser).
 * Without that proof the number is stated as a plain limit. Same rule that
 * stopped OrgCreditPanel printing a 다음 제공일 for a grant that never runs.
 *
 * Rendering rules, in order:
 *   unlimited tier            -> 무제한 / Unlimited       (never "0건")
 *   a number + a proven grant -> 매월 분석 리포트 N건 제공  (the /pricing wording,
 *                                / N analysis reports      via monthlyCreditsLine)
 *                                  each month
 *   a number, grant unproven  -> 분석 리포트 N건           (a limit, no promise)
 *                                / N analysis reports
 *   anything else             -> DASH                     (incident O3: an unknown
 *                                                          value is NEVER a 0)
 *
 * MONTHLY ONLY, deliberately: no "12개월간 총 N건" here. A partial refund
 * shortens the term, so a 12-month total on the customer's own plan could
 * overstate what is left of it. The term total belongs on the sales pages.
 *
 * Exported so the wording can be tested without mounting the whole settings
 * page -- same reason OrgCreditPanel exports resolveNextPeriodStart.
 */
export function planAllowanceText({
  creditLimit = null,
  unlimited = false,
  grantsMonthly = null,
  isKo = false,
} = {}) {
  if (unlimited === true) return isKo ? '무제한' : 'Unlimited';
  // A genuine server-sent 0 still prints 0; null/undefined/NaN do not. See the
  // O3 note in OrgCreditPanel -- an empty allowance and an unknown one are
  // different facts and must never look the same.
  if (typeof creditLimit !== 'number' || !Number.isFinite(creditLimit)) return DASH;
  const n = creditLimit.toLocaleString();
  // One phrasing, shared with '/' and '/pricing' (constants/plans.js). The
  // NUMBER is ours -- server-sourced -- and only the sentence is borrowed.
  if (grantsMonthly === true) return monthlyCreditsLine(n, isKo);
  return isKo ? `분석 리포트 ${n}건` : `${n} analysis reports`;
}

/**
 * The 건수 cell of one activity row: a signed count, 건 in Korean. A value
 * that is not a finite number is the shared dash, never "null" or a 0 (O3).
 * The sign stays (+ provided, - used); the colour does not -- see the table.
 */
function ledgerCount(amount, isKo) {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return DASH;
  return `${amount > 0 ? '+' : ''}${amount.toLocaleString()}${isKo ? '건' : ''}`;
}

function SubscriptionTab() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  // ⚠ T1.4 — `orgCredits`, NOT the old `balance`.
  //
  // This tab used to read GET /credits/balance, which serves
  // `users.credit_balance` — a legacy per-user counter that gates nothing.
  // An analysis spends `organizations.credits_remaining`. The big purple
  // number on the subscription screen was therefore inert: it did not fall
  // when the customer used the product and did not rise when their plan
  // granted credits. /profile/membership now returns the org pool alongside
  // the membership, so this is one call instead of two.
  const [orgCredits, setOrgCredits] = useState(null);
  const [membership, setMembership] = useState(null);
  // ⚠ THE ORG'S TIER, NOT THE VIEWER'S MEMBERSHIP ROW (review 2026-09-24).
  //
  // `unlimited` used to be inferred from `membership?.status === 'active'`, and
  // `membership` is USER-scoped (`WHERE um.user_id = :user_id`,
  // api/profile.py) while every credit figure beside it is ORG-scoped
  // (`um.org_id = o.id`). For an invited colleague — exactly the cohort T6
  // existed to unblock — `membership` is null, so an Enterprise org's
  // unlimited tier went unrecognised and this card printed "999,999 / —" while
  // the same member's analysis page printed "∞ (무제한)". Two screens, one
  // pool, two answers.
  //
  // GET /credits/balance's `membership_tier` is computed ORG-scoped (active,
  // unexpired, membership_tiers.credit_limit IS NULL => is_unlimited) and is
  // ungated, and it is what the analysis page already uses. Reading it here is
  // NOT a return to the T1.4 defect below: only the TIER is taken from it, the
  // figures still come from /profile/membership's `org_credits`.
  const [orgTier, setOrgTier] = useState(null);
  const [orgTierProbed, setOrgTierProbed] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // A paid plan is what makes a refund conceivable at all: a trial row carries
  // price_krw 0 and has no settled payment behind it.
  const isPaidPlan = !!(membership && (membership.price_krw || 0) > 0);

  useEffect(() => {
    (async () => {
      try {
        const [memRes, histRes, balRes] = await Promise.all([
          apiFetch('/api/v1/profile/membership'),
          apiFetch('/api/v1/credits/history?page=1&per_page=10'),
          // Only for `membership_tier` — the ORG-scoped unlimited flag. See the
          // note on `orgTier`.
          apiFetch('/api/v1/credits/balance'),
        ]);
        if (memRes.ok) {
          const d = await memRes.json();
          setMembership(d.membership || null);
          setOrgCredits(d.org_credits || null);
        }
        if (histRes.ok) { const d = await histRes.json(); setHistory(d.history || d.items || []); }
        if (balRes.ok) {
          const d = await balRes.json();
          setOrgTier(d.membership_tier || null);
          // Set ONLY on a 200: `orgTier === null` then means "this org has no
          // active plan membership", which is a fact. If the call failed we do
          // not know that, and the fallback below applies instead.
          setOrgTierProbed(true);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>;

  // Hoisted out of the <OrgCreditPanel> prop it used to be written inline in,
  // because the 플랜 포함 row below has to answer "is this tier unlimited?" the
  // SAME way the panel does. Two copies of this expression on one screen is the
  // drift that T11 existed to end -- one card would print ∞ while the other
  // printed a dash, for the same org. Rationale for the expression itself is on
  // the `unlimited` prop below.
  const orgUnlimited = orgTierProbed
    ? (orgTier != null && orgTier.is_unlimited === true)
    : (membership?.status === 'active' && orgCredits != null && orgCredits.credit_limit === null);

  return (
    <div className="space-y-6">
      {/* T11 — the org pool now renders through the ONE shared panel
          (components/OrgCreditPanel). This card used to hand-roll the same
          four figures with its own missing-value rules; the analysis page and
          the User Management header hand-rolled two more. See the panel's
          header comment for why that had to stop.

          Two facts this card used to state are deliberately gone:
          • "이월되지 않습니다" — T10a (2026-09-24) made the anniversary grant
            additive-aware, so purchased and admin-granted credits DO survive
            it. The blanket no-roll-over claim is now false for part of the
            pool.
          • "이번 달 한도 N" — the allowance is the panel's (월) 분석 리포트
            한도 row (and the denominator of the headline figure whenever the
            pool is not above it), so it is already stated there. The panel
            labels that row 이번 달 제공 only when a call site can pass the
            amount actually granted, which is a different source from the
            ceiling — see its "WHAT `granted` IS" note. */}
      <OrgCreditPanel
        isKo={isKo}
        remaining={orgCredits?.credits_remaining}
        granted={orgCredits?.credit_limit}
        usedThisPeriod={orgCredits?.credits_used_this_period}
        /* Server-decided: see the note in App.js. An active membership term is
           not evidence that a monthly grant will run, and most assignable tiers
           grant nothing. */
        tierGrantsMonthly={orgCredits?.tier_grants_monthly ?? null}
        /* The membership's start_date, NOT today: credit periods run on the
           purchase anniversary (Q2), and this is the only response that
           carries it. Only an ACTIVE membership has a period at all — a
           pending or expired row renews nothing, and passing its start_date
           would print a renewal date for a plan that is not granting.

           `end_date` goes with it and is not optional: the anniversary walk
           would otherwise print a renewal date in the FINAL month of a
           fixed-term plan, which `grant_due_for_membership` refuses the moment
           `today > end_date`. A customer deciding whether to renew was being
           told their credits refill on a date the sweep will skip.

           ⚠ BOTH ARE USER-SCOPED and so are null for every colleague who did
           not personally buy the plan — they get a dash where the purchasing
           admin gets a date. The data exists: the ORG-scoped query in
           api/profile.py already SELECTs `um.start_date` and then drops it
           from the returned `org_credits` dict. Returning it (or, better, a
           computed `next_period_start`) is the fix; it is a backend change. */
        planStartDate={membership?.status === 'active' ? membership.start_date : null}
        planEndDate={membership?.status === 'active' ? membership.end_date : null}
        /* ORG-scoped (see the `orgTier` note). A null `credit_limit` on
           `org_credits` is NOT proof of an unlimited tier — that query LEFT
           JOINs the active membership, so it is also null when the org has no
           active membership at all (trial) — which is why the tier object
           decides, and only its own `is_unlimited`. The user-scoped inference
           survives ONLY as a fallback for a failed tier probe: wrong for a
           colleague, but it is the pre-existing behaviour and it errs toward a
           dash rather than a fabricated ∞. */
        unlimited={orgUnlimited}
        /* The English half is not in the plan: Copy status: pending_ceo (D9). */
        unavailableReason={orgCredits == null
          ? (isKo
            ? '분석 리포트 이용 현황을 불러오지 못했습니다. 새로 고침하거나 잠시 후 다시 시도해 주세요.'
            : 'We could not load your analysis report usage. Reload, or try again in a moment.')
          : null}
      />

      {/* Membership summary (T1.1-T1.3).

          T1.3: the 만료일 row read `membership.expires_at`, a field
          /profile/membership has never returned — it returns `end_date`. The row
          was silently absent for every customer since it was written, which is
          why nobody reported a wrong date: there was no date.

          T1.2: `days_remaining` was computed server-side and thrown away here.
          It is the number a customer considering a refund actually wants. */}
      <VPCard title={isKo ? '멤버십' : 'Membership'}>
        {membership ? (
          <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {/* C1 - migration 047 gave membership_tiers a display_name_en, because
                one column cannot hold '스타터 연간플랜' and 'Starter Yearly Plan'
                at once. Fall back through the Korean name and then the slug, so
                a tier predating 047 still renders something. */}
            <Row
              label={isKo ? '플랜' : 'Plan'}
              value={
                (isKo ? membership.tier_display_name
                      : (membership.tier_display_name_en || membership.tier_display_name))
                || membership.tier_name || '—'
              }
            />

            {/* T1.1 — the annual total with the monthly rate derived from it, so
                the two can never disagree. */}
            {membership.price_krw != null && (
              <Row
                label={isKo ? '요금' : 'Price'}
                value={
                  membership.monthly_rate_krw != null
                    ? (isKo
                        ? `₩${membership.price_krw.toLocaleString()} (₩${membership.monthly_rate_krw.toLocaleString()} / 월)`
                        : `₩${membership.price_krw.toLocaleString()} (₩${membership.monthly_rate_krw.toLocaleString()} / mo)`)
                    : `₩${membership.price_krw.toLocaleString()}`
                }
              />
            )}

            {/* ⚠ THE ENTITLEMENT, DIRECTLY UNDER THE PRICE (owner request,
                2026-09-28). The card stated what the plan COSTS and never what
                it BUYS, on both this tab and the landing pricing card. A tier
                name is not an answer: nothing on this screen told a customer
                that Starter is 200 검증 a month.

                Deliberately placed after 요금 and before 상태, so price and
                entitlement are read as one pair -- that is the whole point of
                the request, and the number is worth much less two rows away
                from the figure it justifies.

                The value comes from the SERVER (org_credits.credit_limit), not
                from a tier table in the browser; see planAllowanceText for why,
                and for why an unknown allowance is a dash and never a 0. It is
                MONTHLY only, with no 12-month total: a partial refund shortens
                the term. */}
            <Row
              label={isKo ? '플랜 포함 분석 리포트' : 'Included with plan'}
              value={planAllowanceText({
                creditLimit: orgCredits?.credit_limit,
                unlimited: orgUnlimited,
                grantsMonthly: orgCredits?.tier_grants_monthly ?? null,
                isKo,
              })}
            />

            {/* ⚠ THE SENTENCE THAT KEEPS THE TWO NUMBERS APART.
                <OrgCreditPanel> sits a few centimetres above this row and its
                headline is 이번 달 남은 분석 리포트 -- the pool that is LEFT.
                This row is what the plan PUTS IN it. On a Starter org that has
                run 63 analyses the screen therefore shows 137 and 200 at once,
                and without this line the honest reading is "the product is
                telling me two different things". Same defect class as the
                figures T11 collapsed into one panel, one level up: not a wrong
                number, a pair of right ones that look like a contradiction. */}
            <p className="text-xs pt-1" style={{ color: 'var(--text-secondary)' }}>
              {isKo
                ? '플랜에 매월 포함되는 분석 리포트 건수입니다. 이번 달 남은 건수는 위 분석 리포트 이용 현황에서 확인하세요.'
                : 'What your plan includes each month. Reports left this month are shown in the Analysis report usage card above.'}
            </p>

            <Row label={isKo ? '상태' : 'Status'} value={membership.status || '—'} />

            {membership.start_date && (
              <Row
                label={isKo ? '시작일' : 'Started'}
                value={new Date(membership.start_date).toLocaleDateString()}
              />
            )}

            {membership.end_date && (
              <Row
                label={isKo ? '만료일' : 'Expires'}
                value={new Date(membership.end_date).toLocaleDateString()}
              />
            )}

            {membership.days_remaining != null && (
              <Row
                label={isKo ? '남은 기간' : 'Time remaining'}
                value={isKo ? `${membership.days_remaining}일` : `${membership.days_remaining} days`}
              />
            )}

            {/* Money that actually moved, not the list price. A partial refund
                already released has to be visible here, or the next refund quote
                looks wrong to the customer. */}
            {membership.paid_krw > 0 && (
              <Row
                label={isKo ? '결제 금액' : 'Amount paid'}
                value={
                  membership.refunded_krw > 0
                    ? (isKo
                        ? `₩${membership.net_paid_krw.toLocaleString()} (₩${membership.paid_krw.toLocaleString()} 결제, ₩${membership.refunded_krw.toLocaleString()} 환불)`
                        : `₩${membership.net_paid_krw.toLocaleString()} (₩${membership.paid_krw.toLocaleString()} paid, ₩${membership.refunded_krw.toLocaleString()} refunded)`)
                    : `₩${membership.paid_krw.toLocaleString()}`
                }
              />
            )}
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{isKo ? '활성 멤버십 없음' : 'No active membership'}</p>
        )}

        {/* Self-serve actions — replaces the legacy mailto-only footnote. */}
        <SubscriptionActions membership={membership} isKo={isKo} />

        {/* T3.3 — self-service refund REQUEST (not execution; see the component
            header).

            C3 (pricing-change-ui-9-6.md, decision D1) — give PLAN SUBSCRIBERS a
            visible refund path. The card previously rendered nothing in three
            separate states, so a paying customer could open this tab and find no
            evidence refunds existed. `alwaysVisible` keeps an entry point on
            screen for them; the card still decides what is actually quotable.

            Gated on a PAID plan, not merely on `membership`: a trial row carries
            price_krw 0 and nothing to refund, and showing it a refund heading
            would be exactly the dead control the original comment warned about.

            NOT offered on the 분석 리포트 이용 현황 card above, deliberately.
            Reports are not a refund unit (D1; plan §2 "Never call a report
            refundable or non-refundable"): plan reports reset monthly and do
            not roll over, and the packages are withdrawn. This flow refunds
            UNUSED MONTHS of the term -- the month's fee -- never a count of
            reports. */}
        {isPaidPlan && (
          <p className="mt-4 mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
            {isKo ? '환불' : 'Refunds'}
          </p>
        )}
        <RefundRequestCard isKo={isKo} alwaysVisible={isPaidPlan} />

        <p className="mt-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
          {isKo ? '문의: hello@whereat.kr' : 'Questions? hello@whereat.kr'}
        </p>
      </VPCard>

      {/* Recent report activity (D-7: relabelled, not removed).

          KG이니시스 review (T2.4/T2.5). This card read as a money ledger:
          '최근 크레딧 내역', a '금액' column, green "+" and red "-", and the
          STORED memo -- English, written by ~15 call sites and carrying the
          LIKE-matched [grant:...] / [intent:...] / [idem:...] markers -- printed
          raw to the customer. It is now a usage log:
            • the 유형 cell is the server's read-time label (`label_ko` /
              `label_en`, credit_service.ledger_label), chosen by locale. A
              label the row does not carry is the neutral DASH -- the other
              locale's label is NOT borrowed, and `description` is NEVER
              printed, under any fallback;
            • the 건수 cell is a signed count, 건 in Korean, in the neutral text
              colour. */}
      <VPCard title={isKo ? '최근 분석 리포트 이용 내역' : 'Recent report activity'}>
        {history.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{isKo ? '내역이 없습니다' : 'No history yet'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ color: 'var(--text-secondary)' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th className="text-left py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>{isKo ? '날짜' : 'Date'}</th>
                  <th className="text-left py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>{isKo ? '유형' : 'Type'}</th>
                  <th className="text-right py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>{isKo ? '건수' : 'Reports'}</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 10).map((h, i) => (
                  <tr key={h.id || i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td className="py-2">{new Date(h.created_at || h.date).toLocaleDateString()}</td>
                    <td className="py-2">{(isKo ? h.label_ko : h.label_en) || DASH}</td>
                    <td className="py-2 text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                      {ledgerCount(h.amount, isKo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </VPCard>
    </div>
  );
}

/* ─── Team Tab ───────────────────────────────────────────── */
function TeamTab() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [members, setMembers] = useState([]);
  const [seatInfo, setSeatInfo] = useState(null); // { seats_used, seats_total, ... }
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('user'); // 'user' = 멤버, 'admin' = 관리자
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null); // { type: 'success'|'error', text }

  // Per-member action state
  const [actingId, setActingId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  const isTeamManager = user?.role === 'admin' || user?.role === 'org_admin'
    || user?.role === 'root' || user?.role === 'site_admin'
    || user?.is_root || user?.can_manage_admins;

  const tierName = (membership?.tier || membership?.tier_name || '').toLowerCase();
  const tierAllowsInvites = ['team', 'studio', 'enterprise'].includes(tierName);

  const reload = useCallback(async () => {
    try {
      const [usersRes, seatsRes, memRes] = await Promise.all([
        apiFetch('/api/v1/admin/users'),
        apiFetch('/api/v1/admin/users/seats').catch(() => null),
        apiFetch('/api/v1/profile/membership').catch(() => null),
      ]);
      if (usersRes.ok) {
        const data = await usersRes.json();
        const raw = Array.isArray(data) ? data : (data?.users || data?.items || []);
        setMembers((raw || []).filter(Boolean));
      }
      if (seatsRes && seatsRes.ok) {
        setSeatInfo(await seatsRes.json());
      }
      if (memRes && memRes.ok) {
        const d = await memRes.json();
        setMembership(d.membership || d);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isTeamManager) { setLoading(false); return; }
    (async () => { await reload(); setLoading(false); })();
  }, [isTeamManager, reload]);

  const validInviteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail);

  // Seat-cap derivation (best-effort across various API shapes)
  const seatsUsed = seatInfo?.seats_used ?? seatInfo?.used ?? members.filter(m => m.is_active !== false).length;
  const seatsTotal = seatInfo?.seats_total ?? seatInfo?.max_seats ?? seatInfo?.total ?? null;
  const atSeatLimit = seatsTotal != null && seatsUsed >= seatsTotal;

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!validInviteEmail || inviting || atSeatLimit) return;
    setInviting(true);
    setInviteMsg(null);
    try {
      const res = await apiFetch('/api/v1/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail.trim(),
          full_name: (inviteName || inviteEmail).trim(),
          role: inviteRole,
        }),
      });
      if (res.status === 403) {
        setInviteMsg({ type: 'error', text: isKo ? '시트 한도에 도달했거나 권한이 없습니다.' : 'Seat limit reached or insufficient permissions.' });
      } else if (res.status === 409) {
        setInviteMsg({ type: 'error', text: isKo ? '이미 가입된 이메일입니다.' : 'Email is already registered.' });
      } else if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setInviteMsg({
          type: 'error',
          // InviteUserRequest's field_validators raise InputValidationError,
          // whose handler answers with an OBJECT detail. Stored raw it
          // reaches JSX below and unmounts the settings page.
          text: errorMessage(d, isKo ? '초대 실패' : 'Invite failed'),
        });
      } else {
        setInviteMsg({ type: 'success', text: isKo ? '초대를 보냈습니다.' : 'Invitation sent.' });
        setInviteEmail('');
        setInviteName('');
        await reload();
      }
    } catch {
      setInviteMsg({ type: 'error', text: isKo ? '네트워크 오류' : 'Network error' });
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (m) => {
    if (!m?.id || actingId) return;
    setActingId(m.id);
    setActionMsg(null);
    try {
      const res = await apiFetch(`/api/v1/admin/users/${m.id}/reset-password`, { method: 'POST' });
      if (!res.ok) throw new Error('failed');
      setActionMsg({ type: 'success', text: isKo ? '재설정 메일을 발송했습니다.' : 'Reset email sent.' });
    } catch {
      setActionMsg({ type: 'error', text: isKo ? '재발송 실패' : 'Resend failed' });
    } finally {
      setActingId(null);
    }
  };

  const handleRemove = async (m) => {
    if (!m?.id || actingId) return;
    if (!window.confirm(isKo
      ? `${m.email} 계정을 비활성화할까요?`
      : `Deactivate ${m.email}?`)) return;
    setActingId(m.id);
    setActionMsg(null);
    try {
      const res = await apiFetch(`/api/v1/admin/users/${m.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('failed');
      await reload();
    } catch {
      setActionMsg({ type: 'error', text: isKo ? '제거 실패' : 'Remove failed' });
    } finally {
      setActingId(null);
    }
  };

  if (loading) return <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>;

  if (!isTeamManager) {
    return (
      <div className="space-y-6">
        <VPCard title={isKo ? '팀 구성원' : 'Team Members'}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {isKo ? '팀 관리 권한이 없습니다. Org-Admin에게 문의하세요.' : 'You do not have team management permissions. Contact your Org-Admin.'}
          </p>
        </VPCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <VPCard title={isKo ? '팀원 초대' : 'Invite Team Member'}>
        {!tierAllowsInvites && tierName && tierName !== '' ? (
          <div style={{ background: '#FEF3C7', borderLeft: '3px solid #D97706', padding: '12px 16px', borderRadius: 4, color: '#92400E' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {isKo ? '팀 멤버 초대는 Team 플랜 이상에서 지원됩니다.' : 'Team invites require the Team plan or higher.'}
            </div>
            <Link to="/settings?tab=subscription" style={{ color: '#92400E', textDecoration: 'underline', fontSize: 13 }}>
              {isKo ? '플랜 업그레이드 →' : 'Upgrade plan →'}
            </Link>
          </div>
        ) : (
          <form onSubmit={submitInvite} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>{isKo ? '이메일' : 'Email'}</Label>
                <input
                  type="email"
                  className="vp-input"
                  maxLength={MAX_LENGTHS.email}
                  placeholder={isKo ? '초대할 팀원 이메일' : 'teammate@example.com'}
                  value={inviteEmail}
                  onChange={e => { setInviteEmail(e.target.value); setInviteMsg(null); }}
                  disabled={inviting || atSeatLimit}
                />
              </div>
              <div>
                <Label>{isKo ? '이름 (선택)' : 'Full name (optional)'}</Label>
                <input
                  type="text"
                  className="vp-input"
                  maxLength={MAX_LENGTHS.display_name}
                  placeholder={isKo ? '예: 홍길동' : 'e.g., Jane Doe'}
                  value={inviteName}
                  onChange={e => setInviteName(e.target.value)}
                  disabled={inviting || atSeatLimit}
                />
              </div>
            </div>

            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label>{isKo ? '역할' : 'Role'}</Label>
                <select
                  className="vp-input"
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value)}
                  disabled={inviting || atSeatLimit}
                  style={{ minWidth: 160 }}
                >
                  <option value="user">{isKo ? '멤버' : 'Member'}</option>
                  <option value="admin">{isKo ? '관리자' : 'Admin'}</option>
                </select>
              </div>
              <div className="ml-auto">
                <button
                  type="submit"
                  disabled={!validInviteEmail || inviting || atSeatLimit}
                  className="text-white"
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #931DA4 0%, #123293 100%)',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: (!validInviteEmail || inviting || atSeatLimit) ? 'not-allowed' : 'pointer',
                    opacity: (!validInviteEmail || inviting || atSeatLimit) ? 0.5 : 1,
                  }}
                >
                  {inviting
                    ? (isKo ? '발송 중...' : 'Sending...')
                    : (isKo ? '초대 보내기' : 'Send invite')}
                </button>
              </div>
            </div>

            {seatsTotal != null && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {isKo
                  ? `${seatsUsed} / ${seatsTotal} 시트 사용 중`
                  : `${seatsUsed} / ${seatsTotal} seats used`}
                {atSeatLimit && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#FEF3C7', borderLeft: '3px solid #D97706', color: '#92400E', borderRadius: 4 }}>
                    {isKo
                      ? '시트 한도에 도달했습니다. 플랜을 업그레이드하면 더 많은 팀원을 초대할 수 있습니다.'
                      : 'Seat limit reached. Upgrade your plan to invite more members.'}{' '}
                    <Link to="/settings?tab=subscription" style={{ color: '#92400E', textDecoration: 'underline' }}>
                      {isKo ? '업그레이드' : 'Upgrade'}
                    </Link>
                  </div>
                )}
              </div>
            )}

            {inviteMsg && (
              <div style={{
                fontSize: 13,
                padding: '8px 12px',
                borderRadius: 4,
                borderLeft: `3px solid ${inviteMsg.type === 'success' ? '#16A34A' : '#B91C1C'}`,
                background: inviteMsg.type === 'success' ? '#DCFCE7' : '#FEE2E2',
                color: inviteMsg.type === 'success' ? '#15803D' : '#991B1B',
              }}>
                {renderable(inviteMsg.text)}
              </div>
            )}
          </form>
        )}
      </VPCard>

      <VPCard title={isKo ? '팀 구성원' : 'Team Members'}>
        {actionMsg && (
          <div style={{
            fontSize: 13,
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 4,
            borderLeft: `3px solid ${actionMsg.type === 'success' ? '#16A34A' : '#B91C1C'}`,
            background: actionMsg.type === 'success' ? '#DCFCE7' : '#FEE2E2',
            color: actionMsg.type === 'success' ? '#15803D' : '#991B1B',
          }}>
            {actionMsg.text}
          </div>
        )}
        {members.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {isKo ? '멤버가 없습니다' : 'No team members'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th className="text-left py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {isKo ? '이름' : 'Name'}
                  </th>
                  <th className="text-left py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {isKo ? '역할' : 'Role'}
                  </th>
                  <th className="text-left py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {isKo ? '상태' : 'Status'}
                  </th>
                  <th className="text-right py-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {isKo ? '액션' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.map((m, i) => {
                  const isMe = m.id === user?.id;
                  const isActive = m.is_active !== false;
                  const initial = String((m.full_name || m.email || '?')).charAt(0).toUpperCase();
                  return (
                    <tr key={m.id || i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                               style={{ backgroundColor: '#931DA4', color: '#fff' }}>
                            {initial}
                          </div>
                          <div>
                            <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                              {m.full_name || m.email}{isMe && (isKo ? ' (나)' : ' (you)')}
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{m.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3" style={{ color: 'var(--text-secondary)' }}>
                        {m.role === 'admin' ? (isKo ? '관리자' : 'Admin') : (isKo ? '멤버' : 'Member')}
                      </td>
                      <td className="py-3">
                        <span style={{
                          fontSize: 12,
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: isActive ? '#DCFCE7' : '#FEE2E2',
                          color: isActive ? '#15803D' : '#991B1B',
                        }}>
                          {isActive
                            ? (isKo ? '활성' : 'Active')
                            : (isKo ? '비활성' : 'Inactive')}
                        </span>
                      </td>
                      <td className="py-3 text-right">
                        {!isMe && isActive && (
                          <div className="inline-flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleResend(m)}
                              disabled={actingId === m.id}
                              style={{
                                fontSize: 12,
                                padding: '4px 10px',
                                background: 'transparent',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                color: 'var(--text-primary)',
                                cursor: actingId === m.id ? 'wait' : 'pointer',
                              }}
                            >
                              {isKo ? '재발송' : 'Resend'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemove(m)}
                              disabled={actingId === m.id}
                              style={{
                                fontSize: 12,
                                padding: '4px 10px',
                                background: 'transparent',
                                border: '1px solid #B91C1C',
                                borderRadius: 4,
                                color: '#B91C1C',
                                cursor: actingId === m.id ? 'wait' : 'pointer',
                              }}
                            >
                              {isKo ? '제거' : 'Remove'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Link to="/admin/users" className="inline-block mt-4 text-sm font-medium" style={{ color: '#931DA4' }}>
          {isKo ? '고급 사용자 관리 →' : 'Advanced user management →'}
        </Link>
      </VPCard>
    </div>
  );
}

/* ─── Notifications Tab ──────────────────────────────────── */
function NotificationsTab() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  return (
    <div className="space-y-6">
      <VPCard title={isKo ? '알림 설정' : 'Notification Preferences'}>
        <div className="space-y-4">
          <ToggleRow label={isKo ? '분석 완료 알림' : 'Analysis Complete'} desc={isKo ? '분석이 완료되면 이메일 알림을 받습니다' : 'Receive email when analysis completes'} defaultOn />
          {/* KG이니시스 review (T2.4). The English half is not in the plan:
              Copy status: pending_ceo (D9). */}
          <ToggleRow label={isKo ? '분석 리포트 소진 임박 알림' : 'Low analysis report alert'} desc={isKo ? '남은 분석 리포트가 얼마 남지 않으면 알려 드립니다' : 'Alert me when only a few analysis reports are left'} defaultOn />
          <ToggleRow label={isKo ? '서비스 공지' : 'Service Announcements'} desc={isKo ? '서비스 업데이트 및 공지를 받습니다' : 'Receive service updates and announcements'} />
        </div>
      </VPCard>
    </div>
  );
}

/* ─── Support Tab ────────────────────────────────────────── */
function SupportTab() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const [type, setType] = useState('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true); setError('');
    try {
      const res = await apiFetch('/api/v1/feedback', {
        method: 'POST',
        body: JSON.stringify({ type, subject, message }),
      });
      if (res.ok) setSent(true);
      else setError(isKo ? '전송 실패' : 'Failed to send');
    } catch { setError('Error'); }
    setSending(false);
  };

  const faqs = [
    { q: isKo ? 'VeriPass란 무엇인가요?' : 'What is VeriPass?', a: isKo ? 'AI 기반 화장품 규정 준수 검증 서비스입니다.' : 'An AI-powered cosmetics compliance verification service.' },
    // KG이니시스 review (T2.4): the unit is a provided service, 분석 리포트 N건.
    // The English half is not in the plan: Copy status: pending_ceo (D9).
    {
      q: isKo ? '분석 리포트는 어떻게 제공되나요?' : 'How are analysis reports provided?',
      a: isKo
        ? '문구를 1회 분석할 때마다 분석 리포트 1건이 사용됩니다. 유료 플랜은 매월 정해진 건수의 분석 리포트가 제공되며, 미사용분은 다음 달로 이월되지 않습니다.'
        : 'Each claim you analyze uses 1 analysis report. Paid plans provide a set number of analysis reports each month, and unused reports do not carry over to the next month.',
    },
    // T0.4/P5 — sourced from constants/legal.js, not written here. This entry
    // was one of the six divergent refund statements.
    { q: isKo ? '환불 정책은?' : 'What is the refund policy?', a: isKo ? REFUND_POLICY_SUMMARY.ko : REFUND_POLICY_SUMMARY.en },
    { q: isKo ? '데이터는 안전한가요?' : 'Is my data secure?', a: isKo ? 'AES-256 암호화로 보호되며 제3자와 공유하지 않습니다.' : 'Protected by AES-256 encryption, never shared with third parties.' },
  ];

  return (
    <div className="space-y-6">
      {/* FAQ */}
      <VPCard title={isKo ? '자주 묻는 질문' : 'FAQ'}>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group">
              <summary className="cursor-pointer font-medium text-sm py-2 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#a100aa' }}>help</span>
                {f.q}
              </summary>
              <p className="pl-6 pb-2 text-sm" style={{ color: 'var(--text-secondary)' }}>{f.a}</p>
            </details>
          ))}
        </div>
      </VPCard>

      {/* Contact form */}
      <VPCard title={isKo ? '문의하기' : 'Contact Support'}>
        {sent ? (
          <div className="text-sm py-4 text-center" style={{ color: '#16A34A' }}>
            <span className="material-symbols-outlined block text-3xl mb-2">check_circle</span>
            {isKo ? '전송되었습니다. 빠른 시일 내에 답변 드리겠습니다.' : 'Message sent. We\'ll respond soon.'}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{isKo ? '유형' : 'Type'}</Label>
              <select className="vp-input" value={type} onChange={e => setType(e.target.value)}>
                <option value="feedback">{isKo ? '피드백' : 'Feedback'}</option>
                <option value="bug">{isKo ? '버그 리포트' : 'Bug Report'}</option>
                <option value="feature">{isKo ? '기능 요청' : 'Feature Request'}</option>
                <option value="billing">{isKo ? '결제 문의' : 'Billing'}</option>
              </select>
            </div>
            <div>
              <Label>{isKo ? '제목' : 'Subject'}</Label>
              <input className="vp-input" value={subject} onChange={e => setSubject(e.target.value)} maxLength={MAX_LENGTHS.short_text} required />
            </div>
            <div>
              <Label>{isKo ? '내용' : 'Message'}</Label>
              <textarea className="vp-input" rows={5} value={message} onChange={e => setMessage(e.target.value)} maxLength={MAX_LENGTHS.long_text} required />
            </div>
            {error && <p className="text-sm" style={{ color: '#F87171' }}>{error}</p>}
            <button type="submit" disabled={sending} className="vp-btn-primary text-sm px-5 py-2">
              {sending ? '…' : isKo ? '전송' : 'Send'}
            </button>
          </form>
        )}
      </VPCard>
    </div>
  );
}

/* ─── Shared UI components ───────────────────────────────── */
/* VPCard / Label / Row moved to ./settings/primitives.jsx so tabs can live
   in their own files. Moved verbatim - 42 call sites depend on the look. */

function ToggleRow({ label, desc, defaultOn }) {
  const [on, setOn] = useState(defaultOn || false);
  return (
    <div className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <div>
        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{label}</div>
        {desc && <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{desc}</div>}
      </div>
      <button
        onClick={() => setOn(!on)}
        className="relative w-10 h-5 rounded-full transition-colors"
        style={{ backgroundColor: on ? '#a100aa' : 'var(--border-color)' }}
      >
        <span className="absolute top-0.5 transition-transform w-4 h-4 rounded-full bg-white" style={{ left: on ? 20 : 2 }} />
      </button>
    </div>
  );
}
